import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BookCard from './BookCard';
import '../styles/NewArrivalsPage.css';

function getRestoredNAPState() {
  try {
    const s = sessionStorage.getItem('opac_nap_back');
    if (!s) return null;
    const parsed = JSON.parse(s);
    sessionStorage.removeItem('opac_nap_back');
    return parsed;
  } catch { return null; }
}

const CURRENT_YEAR = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1;
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i);
const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];
const PAGE_SIZE = 30;

function NewArrivalsPage() {
  const navigate = useNavigate();
  // Read sessionStorage once at first render to restore filter+page state
  const [restored] = useState(getRestoredNAPState);
  const scrollToRef = useRef(restored?.biblioId ? String(restored.biblioId) : null);
  const initialPageRef = useRef(restored?.page || 1);

  const [tab, setTab] = useState(restored?.tab || 'month');
  const [year, setYear] = useState(restored?.year || CURRENT_YEAR);
  const [month, setMonth] = useState(restored?.month || CURRENT_MONTH);
  const [dateFrom, setDateFrom] = useState(restored?.dateFrom || '');
  const [dateTo, setDateTo] = useState(restored?.dateTo || '');

  const [books, setBooks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const monthLabel = useMemo(() => MONTHS.find((m) => m.value === month)?.label || '', [month]);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const fetchBooks = useCallback(async (pageNum) => {
    const offset = (pageNum - 1) * PAGE_SIZE;
    setLoading(true);
    setError(null);

    try {
      const base = `/api/books/new-arrivals-date?limit=${PAGE_SIZE}&offset=${offset}`;
      const query = tab === 'month'
        ? `${base}&year=${year}&month=${month}`
        : `${base}${dateFrom ? `&dateFrom=${dateFrom}` : ''}${dateTo ? `&dateTo=${dateTo}` : ''}`;

      const res = await fetch(query);
      const data = await res.json();

      if (res.ok) {
        setBooks(Array.isArray(data.books) ? data.books : []);
        setTotal(data.total || 0);
        setPage(pageNum);
        setSearched(true);
        return;
      }

      const rawError = (data && data.error) || '';

      if (/book not found/i.test(rawError)) {
        throw new Error('Month-wise endpoint is not active on server yet. Please restart backend to enable accurate month/date filtering.');
      }

      throw new Error(rawError || 'Failed to fetch new arrivals');
    } catch (err) {
      setBooks([]);
      setTotal(0);
      setError(err.message || 'Failed to fetch new arrivals');
    } finally {
      setLoading(false);
    }
  }, [tab, year, month, dateFrom, dateTo]);

  useEffect(() => {
    // On first mount use restored page (if any); subsequent filter changes reset to page 1
    const p = initialPageRef.current;
    initialPageRef.current = 1;
    fetchBooks(p);
  }, [fetchBooks]);

  // Scroll to last-clicked card after books render
  useEffect(() => {
    if (!books.length || !scrollToRef.current) return;
    const id = scrollToRef.current;
    scrollToRef.current = null;
    requestAnimationFrame(() => {
      document.getElementById(`book-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [books]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBooks(1);
  };

  const handleBookClick = (book) => {
    sessionStorage.setItem('opac_nap_back', JSON.stringify({
      tab, year, month, dateFrom, dateTo, page,
      biblioId: book.biblio_id,
    }));
    navigate(`/book/${book.biblio_id}`, { state: { from: 'new-arrivals-page' } });
  };

  return (
    <section className="nap-page">
      <div className="nap-header">
        <button className="nap-back-btn" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1>New Arrivals (Month Wise)</h1>
          <p>Browse by month/year or date range. G-prefix accession numbers are excluded.</p>
        </div>
      </div>

      <div className="nap-tabs">
        <button
          className={`nap-tab ${tab === 'month' ? 'nap-tab--active' : ''}`}
          onClick={() => setTab('month')}
        >By Month / Year</button>
        <button
          className={`nap-tab ${tab === 'range' ? 'nap-tab--active' : ''}`}
          onClick={() => setTab('range')}
        >By Date Range</button>
      </div>

      <form className="nap-filters" onSubmit={handleSearch}>
        {tab === 'month' && (
          <div className="nap-filter-row">
            <label className="nap-label">
              Month
              <select className="nap-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </label>
            <label className="nap-label">
              Year
              <select className="nap-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {YEARS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        {tab === 'range' && (
          <div className="nap-filter-row">
            <label className="nap-label">
              From
              <input
                type="date"
                className="nap-date-input"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
            <label className="nap-label">
              To
              <input
                type="date"
                className="nap-date-input"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
          </div>
        )}

        <button type="submit" className="nap-search-btn" disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {searched && !loading && (
        <div className="nap-result-summary">
          {total > 0
            ? <>Showing <strong>{(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, total)}</strong> of <strong>{total}</strong> books{tab === 'month' ? ` for ${monthLabel} ${year}` : ''}</>
            : `No new arrivals found${tab === 'month' ? ` for ${monthLabel} ${year}` : ''}.`
          }
        </div>
      )}

      {error && <div className="nap-error">{error}</div>}
      {loading && <div className="nap-loading">Loading...</div>}

      {!loading && books.length > 0 && (
        <div className="nap-grid">
          {books.map((book) => (
            <BookCard
              key={book.biblio_id}
              biblioId={book.biblio_id}
              isbn={book.isbn}
              title={book.title}
              author={book.author}
              dateAdded={book.dateaccessioned}
              onClick={() => handleBookClick(book)}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && !loading && (
        <div className="nap-pagination">
          <button className="nap-page-btn" onClick={() => fetchBooks(1)} disabled={page === 1}>{'<<'}</button>
          <button className="nap-page-btn" onClick={() => fetchBooks(page - 1)} disabled={page === 1}>{'< Prev'}</button>
          <span className="nap-page-info">Page {page} of {totalPages}</span>
          <button className="nap-page-btn" onClick={() => fetchBooks(page + 1)} disabled={page === totalPages}>{'Next >'}</button>
          <button className="nap-page-btn" onClick={() => fetchBooks(totalPages)} disabled={page === totalPages}>{'>>'}</button>
        </div>
      )}
    </section>
  );
}

export default NewArrivalsPage;
